import { getReportSkills } from "@memorang/helpers/report";
import type { BaseItemSchema, MixedItemTag } from "@memorang/types";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import MasonryGrid from "../MasonryGrid";
import { SessionReportAnswerCard } from "./SessionReportAnswerCard";
import type { SessionReportItemTypeDetailSelection } from "./SessionReportItemTypeDetailDialog";
import {
	SessionReportPremiumCard,
	type SessionReportPremiumCardContent,
} from "./SessionReportPremiumCard";
import { SessionReportQuestionTypeCard } from "./SessionReportQuestionTypeCard";
import {
	buildSessionReportQuestionTypeCards,
	isQuestionItem,
} from "./SessionReportQuestionTypeCard/helpers";

type Props = {
	tags: MixedItemTag[];
	topics: string[];
	sessionItems: BaseItemSchema[];
	showItemReview: boolean;
	isSummativeTest: boolean;
	premium?: SessionReportPremiumCardContent;
	itemTypesTipSlot?: ReactNode;
	onViewAllItemTypes: (filter?: string) => void;
	onNavigateToItems: () => void;
	onUpgrade?: () => void;
	onReview: (items: BaseItemSchema[]) => void;
	onOpenReviewAnswers: (items: BaseItemSchema[]) => void;
	onSelectQuestionType: (
		selection: SessionReportItemTypeDetailSelection,
	) => void;
	isReviewLoading?: boolean;
};

export const SessionReportCardsSection = ({
	tags,
	topics,
	sessionItems,
	showItemReview,
	isSummativeTest,
	premium,
	itemTypesTipSlot,
	onViewAllItemTypes,
	onNavigateToItems,
	onOpenReviewAnswers,
	onUpgrade,
	onSelectQuestionType,
	isReviewLoading = false,
}: Props) => {
	const { t } = useTranslation();

	const questionTypeCards = buildSessionReportQuestionTypeCards({
		tags,
		topics,
		sessionItems,
		t,
	});

	const firstCardWithRowsIndex = questionTypeCards.findIndex((card) =>
		card.tagItems.some(isQuestionItem),
	);

	const skillTags = getReportSkills(tags, topics);

	return (
		<MasonryGrid>
			{questionTypeCards.map((card, index) => (
				<SessionReportQuestionTypeCard
					key={card.title}
					{...card}
					isSummativeTest={isSummativeTest}
					onViewAll={() => onViewAllItemTypes(card.filter)}
					onRowPress={onSelectQuestionType}
					renderFooter={
						index === firstCardWithRowsIndex && itemTypesTipSlot
							? () => itemTypesTipSlot
							: undefined
					}
				/>
			))}
			{showItemReview ? (
				<SessionReportAnswerCard
					sessionItems={sessionItems}
					isMarkable={isSummativeTest}
					hideSkills={skillTags.length <= 1}
					onOpenReviewAnswers={onOpenReviewAnswers}
					onViewAll={onNavigateToItems}
					isReviewLoading={isReviewLoading}
				/>
			) : null}
			{premium && onUpgrade ? (
				<SessionReportPremiumCard
					key="session-report-premium"
					{...premium}
					onUpgrade={onUpgrade}
				/>
			) : null}
		</MasonryGrid>
	);
};
